import { Helmet } from 'react-helmet-async';
import { AEO_SITE_URL } from '../lib/aeo/constants';
import type { AeoPageFaq } from '../lib/aeo/faqs';

type AeoPageHeadProps = {
  /** Site path e.g. `/self-phone` or full URL */
  path: string;
  faqs?: AeoPageFaq[];
  /** Additional schema.org JSON-LD objects (Product, etc.) */
  extraJsonLd?: Record<string, unknown>[];
};

function faqPageJsonLd(faqs: AeoPageFaq[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };
}

/** Per-page canonical URL + FAQ (and optional Product) JSON-LD for answer engines. */
export default function AeoPageHead({ path, faqs = [], extraJsonLd = [] }: AeoPageHeadProps) {
  const canonical = path.startsWith('http') ? path : `${AEO_SITE_URL}${path}`;
  const schemas: Record<string, unknown>[] = [];

  if (faqs.length > 0) {
    schemas.push(faqPageJsonLd(faqs));
  }
  schemas.push(...extraJsonLd);

  if (schemas.length === 0) {
    return (
      <Helmet>
        <link rel="canonical" href={canonical} />
      </Helmet>
    );
  }

  return (
    <Helmet>
      <link rel="canonical" href={canonical} />
      {schemas.map((schema, index) => (
        <script key={`aeo-ld-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
